import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { useQuery } from 'react-query';

import { ErrorBoundary } from '../ErrorBoundary/ErrorBoundary';

import { baseUrlState, formMetadataState, coordsState } from 'state';
import { Loader, DisplayError } from 'common/components';
import { Main } from 'common/containers';
import { getCurrentPosition } from 'utils';
import { GetLonLatFromAppName, GetFormSchema } from 'api';

export function MainWrapper() {
	const [coords, setCoords] = useRecoilState(coordsState);
	const [baseUrl, setBaseUrl] = useRecoilState(baseUrlState);
	const [{ useGeolocation }, setFormMetadata] = useRecoilState(
		formMetadataState
	);
	const [loaded, setLoaded] = useState(false);
	const { url: urlParams } = useParams();

	const { isLoading, isSuccess, error, data, refetch } = useQuery(
		'GetFormSchema',
		GetFormSchema(urlParams),
		{
			retry: 0,
			refetchOnWindowFocus: false,
			staleTime: Infinity,
			enabled: urlParams !== null, // run query only when url changes from null to something
		}
	);

	useEffect(() => {
		const mounted = async () => {
			const mData = data.data;
			setFormMetadata({
				url: mData.url,
				id: mData.id,
				name: mData.name,
				description: mData.description,
				inputs: mData.inputs,
				useGeofence: mData.useGeofence,
				useGeolocation: mData.useGeolocation,
				useCaptcha: mData.useCaptcha,
				useRateLimiting: mData.useRateLimiting,
				iconName: mData.iconName,
				primaryColor: mData.primaryColor,
				secondaryColor: mData.secondaryColor,
				sysAppname: mData.sysAppname,
			});
			setLoaded(true);
		};
		// if isSuccess then update atom
		if (isSuccess && data) {
			mounted();
		}
	}, [isSuccess, setFormMetadata, setLoaded, setCoords, baseUrl, data]);

	useEffect(() => {
		const mounted = async () => {
			const coordObject = {
				latitude: null,
				longitude: null,
				userAllowed: false,
				locationConfirmed: false,
				isLocationDefined: true,
			};

			if (useGeolocation) {
				try {
					const position = await getCurrentPosition({
						enableHighAccuracy: true,
					});
					const {
						coords: { longitude, latitude },
					} = position;
					coordObject.latitude = latitude;
					coordObject.longitude = longitude;
					coordObject.userAllowed = true;
					coordObject.locationConfirmed = true;
				} catch (e) {
					try {
						if (baseUrl) {
							const { data } = await GetLonLatFromAppName(baseUrl);
							coordObject.latitude = data.lat;
							coordObject.longitude = data.lon;
						}
					} catch (error) {}
				}
				setCoords(coordObject);
			}
		};

		loaded && mounted();
	}, [baseUrl, loaded, useGeolocation]);

	useEffect(() => {
		// if url and baseUrl are set, and they differ, base url has been tempered with - refetch (and throw error if base url doesn't exist!)
		if (urlParams && baseUrl && urlParams !== baseUrl) refetch();
	}, [urlParams, baseUrl, refetch]);

	useEffect(() => {
		setBaseUrl(urlParams);
	}, [urlParams, setBaseUrl]);

	if (error) return <DisplayError error={error.message} />;
	if (isLoading)
		return (
			<>
				<p></p>
				<Loader circular={false} />
			</>
		);

	if (loaded)
		return (
			<ErrorBoundary>
				<Main />
			</ErrorBoundary>
		);

	return null;
}
